import { useCreativeLibraryStore } from "../../store";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchCreativeCollection, updateCreative } from "../../apiService";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import axios, { CancelTokenSource } from "axios";
import { FolderEmptyState } from "../EmptyState";
import { MasonryColumns } from "../MasonryColumn";
import { AdCreative, PublishAdCreative } from "../../types";
import { PollingAdCard } from "@/components/common/adCard";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import BookmarkAction from "../adActions/BookmarkAction";
import PublishAction from "../adActions/PublishAction";
import EditImageAction from "../adActions/EditImageAction";
import EditImageDialogV2 from "../dialog/EditImageDialogV2";
import ReportTitle from "@/screens/analysis/components/report/ReportTitle";
import { useGlobalStore } from "@/GlobalStore";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiFolderOpenLine } from "@remixicon/react";
import ImageSwitcherAction from "@/components/common/adCard/actions/ImageSwitcherAction";
import { cn } from "@/lib/utils";

const FolderDashboard = () => {
  const {
    setFolderAdCreatives,
    setLoadingFolderCreatives,
    folderAdCreatives,
    loadingFolderCreatives,
    selectedSidebarItem,
    folders,
    setSelectedSidebarItem,
  } = useCreativeLibraryStore();
  const { accountId } = useParams();
  const [searchParams] = useSearchParams();
  const { selectedAccount, sidebarCollapsed } = useGlobalStore();
  const folderId = searchParams.get("folderId");

  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const getCreativeCollection = async (folderId?: string) => {
    if (!accountId) return;

    // Cancel previous request if it exists
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel();
    }

    // Create new CancelToken for this request
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      setLoadingFolderCreatives(true);
      const res = await fetchCreativeCollection(accountId, folderId, {
        cancelToken: cancelTokenRef.current,
      });
      setFolderAdCreatives(res.data);
      setLoadingFolderCreatives(false);
    } catch (error) {
      // Don't log error if request was cancelled
      if (!axios.isCancel(error)) {
        console.error("Error fetching creative collection:", error);
        setLoadingFolderCreatives(false);
      }
    }
  };
  // setting initial tab
  useEffect(() => {
    const folder = folders.find((f) => f.id === folderId);
    // check for folder exist and not already selected
    if (folder && selectedSidebarItem?.id !== folder.id)
      setSelectedSidebarItem({
        id: folder.id,
        type: "folder",
        icon: RiFolderOpenLine,
        value: folder.name,
        label: folder.name,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, folderId]);

  useEffect(() => {
    void getCreativeCollection(folderId ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  // Cleanup effect to cancel request on component unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel();
      }
    };
  }, []);

  const [sortOrder, setSortOrder] = useState<"newest-first" | "oldest-first">(
    "newest-first",
  );
  // ===== LOCAL STORAGE CONFIGURATION =====
  const CTA_COPY_STORAGE_KEY = "cta-copy-preference";

  // ===== STATE MANAGEMENT =====
  const [ctaCopyEnabled, setCtaCopyEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(CTA_COPY_STORAGE_KEY);
      return saved !== null ? (JSON.parse(saved) as boolean) : true;
    } catch {
      return true;
    }
  });

  // ===== LOCAL STORAGE OPERATIONS =====
  const saveCtaCopyPreference = useCallback((value: boolean) => {
    try {
      localStorage.setItem(CTA_COPY_STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
      console.error("Failed to save CTA copy preference:", error);
    }
  }, []);

  const orderedAdCreatives = useMemo(() => {
    return [...folderAdCreatives].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at.split(".")[0]) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at.split(".")[0]) : new Date(0);

      if (sortOrder === "newest-first") {
        return dateB.getTime() - dateA.getTime();
      }
      return dateA.getTime() - dateB.getTime();
    });
  }, [folderAdCreatives, sortOrder]);

  const displayText =
    sortOrder === "newest-first" ? "Latest to Oldest" : "Oldest to Latest";

  const handleBookmarkCreative = async (
    adCreative: AdCreative,
    folderId: string | null,
  ) => {
    // optimistic approach
    if (!accountId) return;
    const _oldAdCreatives = [...folderAdCreatives];
    try {
      setFolderAdCreatives(folderAdCreatives.filter((c) => c.id !== adCreative.id));
      await updateCreative(accountId, adCreative.id, folderId);
    } catch (error) {
      setFolderAdCreatives(_oldAdCreatives); // reverts changes if api fails
      console.error("Error updating creative:", error);
    }
  };

  const handlePollingComplete = (creative: AdCreative) => {
    const updatedFolderAdCreatives = folderAdCreatives.map((existingCreative) => {
      if (existingCreative.id === creative.id) {
        return creative; // Replace with the updated creative from polling
      }
      return existingCreative;
    });
    setFolderAdCreatives(updatedFolderAdCreatives);
  };

  const handleOnPublish = (publishAdCreative: PublishAdCreative) => {
    const base = publishAdCreative.ad_creative;
    if (!base) return;
    const updatedAdCreatives = folderAdCreatives.map((existingAdCreative) =>
      existingAdCreative.id === base.id ? base : existingAdCreative,
    );
    setFolderAdCreatives(updatedAdCreatives);
  };

  // Edit Image V2 (Edit button)
  const [openEditDialogV2, setOpenEditDialogV2] = useState<boolean>(false);
  const [editingAdV2, setEditingAdV2] = useState<AdCreative | null>(null);
  const [currentImageIndexByCreativeId, setCurrentImageIndexByCreativeId] = useState<
    Record<string, number>
  >({});

  const handleEditDialogV2Open = (open: boolean, ad?: AdCreative) => {
    setOpenEditDialogV2(open);
    setEditingAdV2(ad || null);
  };

  const handleEditDialogV2Close = (open: boolean, activeImageId?: string) => {
    if (!open && editingAdV2 && activeImageId) {
      const availableImages = editingAdV2.creative?.images || [];
      const newIndex = availableImages.findIndex((img) => img.id === activeImageId);
      if (newIndex !== -1) {
        setCurrentImageIndexByCreativeId((prev) => ({
          ...prev,
          [editingAdV2.id]: newIndex,
        }));
      }
    }
    setOpenEditDialogV2(open);
    if (!open) {
      setEditingAdV2(null);
    }
  };

  // Update folder dashboard when creative is updated from EditImageDialogV2
  const handleDialogCreativeUpdate = (updated: AdCreative) => {
    setFolderAdCreatives(
      folderAdCreatives.map((ad) => (ad.id === updated.id ? updated : ad)),
    );
    setEditingAdV2((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  // ===== EVENT HANDLERS =====
  const handleCtaCopyToggle = useCallback(
    (newValue: boolean) => {
      setCtaCopyEnabled(newValue);
      saveCtaCopyPreference(newValue);
    },
    [saveCtaCopyPreference],
  );

  return (
    <div className="flex flex-col pl-[9px] w-full h-full">
      <div className="flex gap-2 pt-[8px] pb-2 items-center justify-between pr-2">
        {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="sq-outline" className="flex items-center gap-2 w-[154px]">
              <ArrowUpDown
                size={14}
                className="text-brand-primary-400"
                strokeWidth={1.8}
              />
              <span className="font-['Helvetica_Neue'] text-[11px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-800">
                {displayText}
              </span>
              <ChevronDown
                size={14}
                className="text-brand-primary-400"
                strokeWidth={1.8}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[154px] !rounded-[8px]">
            <DropdownMenuItem
              onClick={() => setSortOrder("newest-first")}
              className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
            >
              Latest to Oldest
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setSortOrder("oldest-first")}
              className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
            >
              Oldest to Latest
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Toggle Switch Container */}
        <button
          onClick={() => handleCtaCopyToggle(!ctaCopyEnabled)}
          className="relative h-8 flex items-center gap-2 bg-transparent squircle squircle-fill-[white] hover:squircle-fill-hover-gray drop-shadow-sm px-3 py-1.5 cursor-pointer 
          before:absolute before:inset-0 before:squircle before:squircle-radius-[8px] before:squircle-outline-[1px] before:squircle-outline-color-border-gray before:pointer-events-none"
        >
          <Switch
            checked={ctaCopyEnabled}
            onCheckedChange={handleCtaCopyToggle}
            className="h-4 w-7 data-[state=unchecked]:bg-border-gray data-[state=checked]:bg-brand-orange pointer-events-none"
            thumbClassName="h-3 w-3 data-[state=checked]:translate-x-3 data-[state=unchecked]:translate-x-0.1"
          />
          <span className="text-[12px] font-normal text-brand-primary-new/80">
            CTA + copy
          </span>
        </button>
      </div>
      <div className="flex flex-col overflow-y-auto mb-4 h-full">
        {/* Title */}
        <div className="flex items-center gap-2 mt-5">
          <ReportTitle
            title={selectedSidebarItem?.label ?? ""}
            icon={
              selectedSidebarItem && (
                <selectedSidebarItem.icon size={16} className="text-brand-primary-800" />
              )
            }
            iconBackgroundColor="#F6F6F6"
          />
        </div>
        {/* creatives */}
        {loadingFolderCreatives ? (
          <div className="flex items-center justify-center h-[80%] w-full">
            <PageLoader />
          </div>
        ) : orderedAdCreatives.length === 0 ? (
          <FolderEmptyState
            className="-translate-y-[70px] -translate-x-[32px]"
            heading="Folder is Empty"
            description="Save creatives from Explore to see them here."
          />
        ) : (
          <div className="mt-6">
            <MasonryColumns
              items={orderedAdCreatives}
              cardWidth={296}
              cardGap={14}
              renderItem={(adCreative) => {
                const availableImages = adCreative.creative?.images || [];
                const hasMultipleImages = availableImages.length > 1;
                const currentImageIndex =
                  currentImageIndexByCreativeId[adCreative.id] || 0;
                const currentImage = availableImages[currentImageIndex];

                return (
                  <PollingAdCard
                    key={`${adCreative.id}`}
                    accountIconUrl={selectedAccount?.additional_info?.ad_account_icon_url}
                    ctaCopyEnabled={ctaCopyEnabled}
                    className={cn(
                      "min-w-[296px] flex-shrink-0",
                      !ctaCopyEnabled && "p-2 rounded-[16px]",
                    )}
                    adCreative={adCreative}
                    onCreativeUpdate={handleDialogCreativeUpdate}
                    calculateHeight={true}
                    currentImageIndex={currentImageIndex}
                    mediaActions={[
                      {
                        component: (
                          <DownloadAction
                            url={
                              adCreative.creative_type === "video"
                                ? adCreative.creative?.videos?.[0]?.permalink_url
                                : currentImage?.uncompressed_url ||
                                  currentImage?.permalink_url
                            }
                            type={adCreative.creative_type}
                            aspectRatio={
                              adCreative.creative_type === "video"
                                ? adCreative.creative?.videos?.[0]?.aspect_ratio
                                : currentImage?.aspect_ratio
                            }
                          />
                        ),
                        position: "top-right",
                        visibility: "hover",
                      },
                      {
                        component: (
                          <BookmarkAction
                            folderId={adCreative.folder_id}
                            onBookmark={(folderId) =>
                              void handleBookmarkCreative(adCreative, folderId)
                            }
                          />
                        ),
                        position: "top-right",
                        visibility: "always",
                      },
                      ...(adCreative.creative_type === "image" && hasMultipleImages
                        ? [
                            {
                              component: (
                                <ImageSwitcherAction
                                  currentIndex={currentImageIndex}
                                  totalImages={availableImages.length}
                                  onPrevious={() => {
                                    if (currentImageIndex > 0) {
                                      setCurrentImageIndexByCreativeId((prev) => ({
                                        ...prev,
                                        [adCreative.id]: currentImageIndex - 1,
                                      }));
                                    }
                                  }}
                                  onNext={() => {
                                    if (currentImageIndex < availableImages.length - 1) {
                                      setCurrentImageIndexByCreativeId((prev) => ({
                                        ...prev,
                                        [adCreative.id]: currentImageIndex + 1,
                                      }));
                                    }
                                  }}
                                />
                              ),
                              position: "top-left" as const,
                              visibility: "always-hover" as const,
                            },
                          ]
                        : []),
                      {
                        component: (
                          <PublishAction
                            published={!!adCreative?.publish_creative?.status_message}
                            creativeId={adCreative?.id}
                            statusMessage={adCreative?.publish_creative?.status_message}
                            onPublish={(publishAdCreative) =>
                              handleOnPublish(publishAdCreative)
                            }
                          />
                        ),
                        position: "bottom-left",
                        visibility: "hover",
                      },
                      ...(adCreative.creative_type === "image"
                        ? [
                            {
                              component: (
                                <EditImageAction
                                  onClick={() => handleEditDialogV2Open(true, adCreative)}
                                />
                              ),
                              position: "bottom-right" as const,
                              visibility: "hover" as const,
                            },
                          ]
                        : []),
                    ]}
                    onPollingComplete={handlePollingComplete}
                  />
                );
              }}
            />
          </div>
        )}
      </div>
      {openEditDialogV2 && (
        <EditImageDialogV2
          open={openEditDialogV2}
          onOpenChange={handleEditDialogV2Close}
          adCreative={editingAdV2}
          onCreativeUpdate={handleDialogCreativeUpdate}
          initialActiveImageId={
            editingAdV2
              ? (() => {
                  const availableImages = editingAdV2.creative?.images || [];
                  const currentIndex = currentImageIndexByCreativeId[editingAdV2.id] || 0;
                  return availableImages[currentIndex]?.id;
                })()
              : undefined
          }
        />
      )}
    </div>
  );
};

export default FolderDashboard;
